/**
 * Secret redaction for user-facing error output.
 *
 * Masks token-shaped strings before text reaches a surface a human can read or
 * forward: console (stderr/stdout) and Sentry. The dominant leak vector is a
 * backend (or AuthKit) error-response body interpolated verbatim into an error
 * message — e.g. `ApiError.message`/`.body`, which dozens of commands print.
 *
 * Standalone leaf module — NO imports — so `api-client.ts` can redact inside the
 * `ApiError` constructor without an import cycle (mirrors the frontend
 * `api-error.ts` leaf-module pattern).
 */

const PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  // JWTs (header.payload.signature) — WorkOS access/refresh tokens, OAuth JWTs.
  // Run first so a `Bearer <jwt>` collapses to the JWT placeholder.
  [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[REDACTED_JWT]'],
  // way_ scoped API tokens (keyring/CI credentials). Leading \b avoids
  // over-redacting benign substrings (e.g. `gateway_config`); the charset is
  // base64url (no `.`) so a trailing sentence period stays outside the match.
  [/\bway_[A-Za-z0-9_-]{8,}/g, 'way_[REDACTED]'],
  // wst_ single-use WebSocket tickets (same opaque base64url format).
  [/\bwst_[A-Za-z0-9_-]{8,}/g, 'wst_[REDACTED]'],
  // Authorization: Bearer <opaque token> (anything not already masked above).
  [/(Bearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi, '$1[REDACTED]'],
];

/**
 * Replace token-shaped secrets in `text` with stable placeholders, preserving
 * the surrounding (non-secret) message so the error stays useful. Returns the
 * input unchanged when it contains no token-shaped strings.
 */
export function redactSecrets(text: string): string {
  let out = text;
  for (const [pattern, replacement] of PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}
